<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TemplateForm from './TemplateForm.vue';

const store = useStore();
const { t } = useI18n();

const selectedInboxId = ref(null);
const searchQuery = ref('');
const statusFilter = ref('');
const categoryFilter = ref('');

const STATUS_OPTIONS = [
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'DISABLED', label: 'Disabled' },
];
const CATEGORY_OPTIONS = [
  { value: 'UTILITY', label: 'Utility' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
];
const showCreateModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const templateToEdit = ref(null);
const templateToDelete = ref(null);

const whatsappCloudInboxes = computed(() => {
  const inboxes = store.getters['inboxes/getInboxes'];
  return inboxes.filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === 'whatsapp_cloud'
  );
});

const selectedInbox = computed(() => {
  if (!selectedInboxId.value) return null;
  return whatsappCloudInboxes.value.find(
    inbox => inbox.id === selectedInboxId.value
  );
});

const templates = computed(
  () => store.getters['whatsappTemplates/getTemplates']
);
const uiFlags = computed(() => store.getters['whatsappTemplates/getUIFlags']);

const filteredTemplates = computed(() => {
  let result = templates.value;
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    result = result.filter(
      tmpl =>
        tmpl.name?.toLowerCase().includes(q) ||
        tmpl.components?.some(c => c.text?.toLowerCase().includes(q))
    );
  }
  if (statusFilter.value) {
    result = result.filter(tmpl => tmpl.status === statusFilter.value);
  }
  if (categoryFilter.value) {
    result = result.filter(tmpl => tmpl.category === categoryFilter.value);
  }
  return result;
});

const lastSyncedAt = computed(() => {
  const ts = selectedInbox.value?.message_templates_last_updated;
  if (!ts) return null;
  return new Date(ts).toLocaleString();
});

// Business Account ID only reaches the client via provider_config (admins only),
// so the Business Manager deep-link shows up only when it's available.
const businessManagerUrl = computed(() => {
  const businessAccountId =
    selectedInbox.value?.provider_config?.business_account_id;
  if (!businessAccountId) return null;
  return `https://business.facebook.com/wa/manage/home/?business_id=${businessAccountId}`;
});

const templateStats = computed(() => {
  const all = templates.value;
  return {
    total: all.length,
    approved: all.filter(tmpl => tmpl.status === 'APPROVED').length,
    pending: all.filter(tmpl => tmpl.status === 'PENDING').length,
    rejected: all.filter(tmpl => tmpl.status === 'REJECTED').length,
  };
});

const fetchTemplates = async () => {
  if (!selectedInboxId.value) return;
  try {
    await store.dispatch('whatsappTemplates/fetchTemplates', {
      inboxId: selectedInboxId.value,
    });
  } catch (error) {
    useAlert(error?.message || t('WHATSAPP_TEMPLATES.MANAGER.FETCH_ERROR'));
  }
};

const syncTemplates = async () => {
  try {
    await store.dispatch('whatsappTemplates/syncTemplates');
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('WHATSAPP_TEMPLATES.MANAGER.SYNC_ERROR'));
  }
};

const editTemplate = tmpl => {
  templateToEdit.value = tmpl;
  showEditModal.value = true;
};

const onTemplateUpdated = async () => {
  showEditModal.value = false;
  templateToEdit.value = null;
  await fetchTemplates();
  useAlert(t('WHATSAPP_TEMPLATES.MANAGER.UPDATE_SUCCESS'));
};

const confirmDelete = tmpl => {
  templateToDelete.value = tmpl;
  showDeleteModal.value = true;
};

const deleteTemplate = async () => {
  try {
    await store.dispatch('whatsappTemplates/deleteTemplate', {
      name: templateToDelete.value.name,
      // id é o hsm_id por idioma — deleta apenas a variação clicada.
      hsmId: templateToDelete.value.id,
    });
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.DELETE_SUCCESS'));
    showDeleteModal.value = false;
  } catch (error) {
    useAlert(error?.message || t('WHATSAPP_TEMPLATES.MANAGER.DELETE_ERROR'));
  }
};

const onTemplateCreated = async () => {
  showCreateModal.value = false;
  await fetchTemplates();
  useAlert(t('WHATSAPP_TEMPLATES.MANAGER.CREATE_SUCCESS'));
};

const statusBadgeClass = status => {
  const map = {
    APPROVED: 'bg-n-teal-3 text-n-teal-11 border border-n-teal-4',
    PENDING: 'bg-n-amber-3 text-n-amber-11 border border-n-amber-4',
    REJECTED: 'bg-n-ruby-3 text-n-ruby-11 border border-n-ruby-4',
    PAUSED: 'bg-n-amber-3 text-n-amber-11 border border-n-amber-4',
    DISABLED: 'bg-n-slate-3 text-n-slate-11 border border-n-slate-4',
  };
  return map[status] || 'bg-n-slate-3 text-n-slate-11 border border-n-slate-4';
};

const categoryBadgeClass = category => {
  const map = {
    UTILITY: 'bg-n-blue-3 text-n-blue-11 border border-n-blue-4',
    MARKETING: 'bg-n-violet-3 text-n-violet-11 border border-n-violet-4',
    AUTHENTICATION: 'bg-n-slate-3 text-n-slate-11 border border-n-slate-4',
  };
  return (
    map[category] || 'bg-n-slate-3 text-n-slate-11 border border-n-slate-4'
  );
};

const categoryIcon = category => {
  const map = {
    UTILITY: 'i-lucide-wrench',
    MARKETING: 'i-lucide-megaphone',
    AUTHENTICATION: 'i-lucide-shield-check',
  };
  return map[category] || 'i-lucide-file-text';
};

const getTemplateBody = tmpl => {
  const body = tmpl.components?.find(c => c.type === 'BODY');
  return body?.text || '';
};

const getTemplateHeader = tmpl => {
  const header = tmpl.components?.find(c => c.type === 'HEADER');
  return header?.text || '';
};

const getButtonCount = tmpl => {
  const buttons = tmpl.components?.find(c => c.type === 'BUTTONS');
  return buttons?.buttons?.length || 0;
};

// M7: light polling while any template is PENDING. The M1 status webhook updates
// channel.message_templates server-side in near real-time, so a low-frequency
// re-fetch surfaces APPROVED/REJECTED without the user clicking Sync. Polling
// stops as soon as nothing is pending (or the page is left).
const POLL_INTERVAL_MS = 15000;
let pollTimer = null;

const hasPendingTemplates = computed(() => templateStats.value.pending > 0);

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const startPolling = () => {
  if (pollTimer || !selectedInboxId.value) return;
  pollTimer = setInterval(() => {
    if (!hasPendingTemplates.value || !selectedInboxId.value) {
      stopPolling();
      return;
    }
    fetchTemplates();
  }, POLL_INTERVAL_MS);
};

watch(hasPendingTemplates, isPending => {
  if (isPending) startPolling();
  else stopPolling();
});

watch(selectedInboxId, () => {
  stopPolling();
  store.dispatch('whatsappTemplates/setSelectedInbox', selectedInboxId.value);
  fetchTemplates();
});

onBeforeUnmount(stopPolling);

onMounted(() => {
  store.dispatch('inboxes/get');
  if (whatsappCloudInboxes.value.length === 1) {
    selectedInboxId.value = whatsappCloudInboxes.value[0].id;
  }
});
</script>

<template>
  <div
    class="flex flex-col w-full h-full overflow-hidden bg-n-background relative"
  >
    <!-- Header: chrome bleeds via SettingsWrapper layout; content tracks via max-w-7xl -->
    <div class="px-6 lg:px-8 pt-6 pb-4 w-full">
      <div class="max-w-7xl mx-auto w-full">
        <BaseSettingsHeader
          :title="t('WHATSAPP_TEMPLATES.MANAGER.TITLE')"
          :description="t('WHATSAPP_TEMPLATES.MANAGER.DESCRIPTION')"
        >
          <template v-if="selectedInboxId" #actions>
            <button
              class="hidden sm:inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-n-slate-12 bg-n-background hover:bg-n-alpha-1/50 border border-n-strong rounded-xl shadow-sm hover:shadow-md hover:-translate-y-px active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-n-brand/20 whitespace-nowrap"
              :aria-label="t('WHATSAPP_TEMPLATES.MANAGER.SYNC')"
              @click="syncTemplates"
            >
              <span
                v-if="uiFlags.isSyncing"
                class="i-lucide-loader-2 size-4.5 animate-spin"
                aria-hidden="true"
              />
              <span
                v-else
                class="i-lucide-refresh-cw size-4.5"
                aria-hidden="true"
              />
              {{ t('WHATSAPP_TEMPLATES.MANAGER.SYNC') }}
            </button>
            <button
              class="sm:hidden inline-flex items-center justify-center size-10 text-n-slate-12 bg-n-background hover:bg-n-alpha-1/50 border border-n-strong rounded-xl shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-n-brand/20"
              :aria-label="t('WHATSAPP_TEMPLATES.MANAGER.SYNC')"
              @click="syncTemplates"
            >
              <span
                v-if="uiFlags.isSyncing"
                class="i-lucide-loader-2 size-4.5 animate-spin"
                aria-hidden="true"
              />
              <span
                v-else
                class="i-lucide-refresh-cw size-4.5"
                aria-hidden="true"
              />
            </button>

            <button
              class="hidden sm:inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-n-brand hover:bg-n-brand/90 rounded-xl shadow-sm hover:shadow-lg hover:shadow-n-brand/20 hover:-translate-y-px active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-n-brand whitespace-nowrap ml-2"
              :aria-label="t('WHATSAPP_TEMPLATES.MANAGER.CREATE')"
              @click="showCreateModal = true"
            >
              <span class="i-lucide-plus size-4.5" aria-hidden="true" />
              {{ t('WHATSAPP_TEMPLATES.MANAGER.CREATE') }}
            </button>
            <button
              class="sm:hidden inline-flex items-center justify-center size-10 text-white bg-n-brand hover:bg-n-brand/90 rounded-xl shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-n-brand ml-2"
              :aria-label="t('WHATSAPP_TEMPLATES.MANAGER.CREATE')"
              @click="showCreateModal = true"
            >
              <span class="i-lucide-plus size-4.5" aria-hidden="true" />
            </button>
          </template>
        </BaseSettingsHeader>
      </div>
    </div>

    <!-- Toolbar: chrome bleeds (bg + border full-width), content tracks max-w-7xl.
         Single-row em desktop: inbox + search + filters + stats inline. -->
    <div class="border-b border-n-weak/50 bg-n-alpha-1/10 w-full">
      <div class="max-w-7xl mx-auto w-full px-6 lg:px-8 py-4">
        <div
          class="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 lg:gap-4"
        >
          <!-- Inbox Selector -->
          <div class="relative w-full lg:w-[280px] shrink-0">
            <div
              class="flex items-center gap-2 px-3 py-2 rounded-xl border border-n-strong bg-n-background hover:bg-n-alpha-1/50 focus-within:ring-2 focus-within:ring-n-brand/20 focus-within:border-n-brand transition-all shadow-sm"
            >
              <Icon
                icon="i-ri-whatsapp-line"
                class="size-5 shrink-0"
                :class="selectedInbox ? 'text-[#25D366]' : 'text-n-slate-10'"
              />
              <select
                id="whatsapp-inbox-select"
                v-model="selectedInboxId"
                :aria-label="t('WHATSAPP_TEMPLATES.MANAGER.SELECT_INBOX')"
                class="flex-1 min-w-0 px-0 py-0 !m-0 h-auto border-0 bg-transparent text-sm font-semibold text-n-slate-12 focus:ring-0 focus:outline-none cursor-pointer appearance-none"
              >
                <option :value="null" disabled>
                  {{ t('WHATSAPP_TEMPLATES.MANAGER.SELECT_INBOX_PLACEHOLDER') }}
                </option>
                <option
                  v-for="inbox in whatsappCloudInboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                  {{ inbox.phone_number ? `(${inbox.phone_number})` : '' }}
                </option>
              </select>
              <span
                v-if="selectedInbox"
                class="hidden sm:inline-flex items-center justify-center size-2 rounded-full bg-[#25D366] shrink-0 animate-pulse ring-2 ring-[#25D366]/20"
                title="Connected"
              />
              <Icon
                icon="i-lucide-chevron-down"
                class="size-4 shrink-0 text-n-slate-10 pointer-events-none"
              />
            </div>
          </div>

          <!-- Filters + Stats inline (visible only when inbox selected) -->
          <template v-if="selectedInboxId">
            <!-- Search -->
            <div class="relative w-full sm:w-64 group">
              <Icon
                icon="i-lucide-search"
                class="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 group-focus-within:text-n-brand transition-colors pointer-events-none"
              />
              <input
                v-model="searchQuery"
                type="search"
                :aria-label="t('WHATSAPP_TEMPLATES.MANAGER.SEARCH')"
                :placeholder="t('WHATSAPP_TEMPLATES.MANAGER.SEARCH')"
                class="!mb-0 w-full border border-n-strong rounded-xl !pl-10 !pr-4 py-2 text-sm font-medium bg-n-background text-n-slate-12 transition-all shadow-sm hover:bg-n-alpha-1/50 focus:bg-n-background focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand placeholder-n-slate-9"
              />
            </div>

            <!-- Status + Category filters -->
            <div
              class="grid grid-cols-2 gap-3 sm:flex sm:items-center sm:gap-3"
            >
              <div class="relative sm:w-40 group">
                <select
                  v-model="statusFilter"
                  :aria-label="t('WHATSAPP_TEMPLATES.MANAGER.ALL_STATUS')"
                  class="!mb-0 appearance-none !bg-none w-full border border-n-strong rounded-xl pl-4 pr-10 py-2 text-sm font-medium bg-n-background text-n-slate-12 transition-all shadow-sm hover:bg-n-alpha-1/50 focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand cursor-pointer"
                >
                  <option value="">
                    {{ t('WHATSAPP_TEMPLATES.MANAGER.ALL_STATUS') }}
                  </option>
                  <option
                    v-for="opt in STATUS_OPTIONS"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
                <span
                  class="i-lucide-chevron-down absolute right-4 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 group-focus-within:text-n-brand transition-colors pointer-events-none"
                />
              </div>

              <div class="relative sm:w-40 group">
                <select
                  v-model="categoryFilter"
                  :aria-label="t('WHATSAPP_TEMPLATES.MANAGER.ALL_CATEGORIES')"
                  class="!mb-0 appearance-none !bg-none w-full border border-n-strong rounded-xl pl-4 pr-10 py-2 text-sm font-medium bg-n-background text-n-slate-12 transition-all shadow-sm hover:bg-n-alpha-1/50 focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand cursor-pointer"
                >
                  <option value="">
                    {{ t('WHATSAPP_TEMPLATES.MANAGER.ALL_CATEGORIES') }}
                  </option>
                  <option
                    v-for="opt in CATEGORY_OPTIONS"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
                <span
                  class="i-lucide-chevron-down absolute right-4 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 group-focus-within:text-n-brand transition-colors pointer-events-none"
                />
              </div>
            </div>

            <!-- Stats inline (right-aligned on desktop, wrap on mobile) -->
            <div
              v-if="templates.length > 0"
              class="flex flex-wrap items-center gap-2 text-[11px] font-bold tracking-wide uppercase lg:ml-auto"
            >
              <span class="text-n-slate-11 bg-n-alpha-2 px-2 py-1 rounded-md">
                {{ templateStats.total }} TOTAL
              </span>
              <span
                class="text-n-teal-11 bg-n-teal-3 border border-n-teal-4 px-2 py-1 rounded-md"
              >
                {{ templateStats.approved }} APPROVED
              </span>
              <span
                v-if="templateStats.pending > 0"
                class="text-n-amber-11 bg-n-amber-3 border border-n-amber-4 px-2 py-1 rounded-md"
              >
                {{ templateStats.pending }} PENDING
              </span>
              <span
                v-if="templateStats.rejected > 0"
                class="text-n-ruby-11 bg-n-ruby-3 border border-n-ruby-4 px-2 py-1 rounded-md"
              >
                {{ templateStats.rejected }} REJECTED
              </span>
            </div>
          </template>
        </div>

        <!-- M2: sync metadata — last sync time + Business Manager deep-link -->
        <div
          v-if="selectedInboxId && (lastSyncedAt || businessManagerUrl)"
          class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[11px] font-medium text-n-slate-10"
        >
          <span v-if="lastSyncedAt" class="inline-flex items-center gap-1.5">
            <Icon icon="i-lucide-refresh-cw" class="size-3" />
            Última sincronização: {{ lastSyncedAt }}
          </span>
          <a
            v-if="businessManagerUrl"
            :href="businessManagerUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 text-n-brand hover:underline"
          >
            <Icon icon="i-lucide-external-link" class="size-3" />
            Abrir no Business Manager
          </a>
        </div>
      </div>
    </div>

    <!-- Content Area (Scrollable) — chrome bleeds, content tracks max-w-7xl -->
    <div
      class="flex-1 overflow-y-auto relative custom-scrollbar bg-n-background"
    >
      <div class="max-w-7xl mx-auto w-full px-6 lg:px-8 py-6">
        <!-- Premium Loading Skeletons -->
        <div
          v-if="uiFlags.isFetching"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          <div
            v-for="i in 8"
            :key="`skeleton-${i}`"
            class="h-48 rounded-[1.25rem] bg-gradient-to-br from-n-alpha-1 to-n-alpha-2 animate-pulse ring-1 ring-black/5"
          >
            <div class="p-5 flex flex-col gap-4 h-full">
              <div class="flex items-center justify-between">
                <div class="size-10 rounded-[1rem] bg-n-background/50" />
                <div class="h-6 w-16 bg-n-background/50 rounded-lg" />
              </div>
              <div class="space-y-2 flex-1 mt-2">
                <div class="h-4 bg-n-background/50 rounded w-full" />
                <div class="h-4 bg-n-background/50 rounded w-5/6" />
                <div class="h-4 bg-n-background/50 rounded w-2/3" />
              </div>
            </div>
          </div>
        </div>

        <!-- No Inbox Selected State Premium -->
        <Transition
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="opacity-0 translate-y-4"
        >
          <div
            v-if="!selectedInboxId && !uiFlags.isFetching"
            class="flex flex-col items-center pt-12 pb-8 text-center max-w-md mx-auto"
          >
            <template v-if="!whatsappCloudInboxes.length">
              <div
                class="size-20 rounded-3xl bg-n-amber-2 text-n-amber-11 flex items-center justify-center mb-5 ring-1 ring-n-amber-4 shadow-inner"
              >
                <Icon icon="i-lucide-triangle-alert" class="size-10" />
              </div>
              <h3 class="text-lg font-bold text-n-slate-12 mb-2 tracking-tight">
                {{ t('WHATSAPP_TEMPLATES.MANAGER.NO_CLOUD_INBOXES') }}
              </h3>
              <p
                class="text-sm font-medium text-n-slate-10 max-w-sm leading-relaxed mb-6"
              >
                {{ t('WHATSAPP_TEMPLATES.MANAGER.DESCRIPTION') }}
              </p>
              <router-link :to="{ name: 'settings_inbox_list' }">
                <button
                  class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-n-brand hover:bg-n-brand/90 rounded-xl shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-n-brand hover:-translate-y-px active:translate-y-0"
                >
                  <span class="i-lucide-plus size-4.5" aria-hidden="true" />
                  {{ t('WHATSAPP_TEMPLATES.MANAGER.CREATE_INBOX_LINK') }}
                </button>
              </router-link>
            </template>

            <template v-else>
              <div
                class="size-20 rounded-3xl bg-[#25D366]/10 text-[#25D366] flex items-center justify-center mb-5 ring-1 ring-[#25D366]/20 shadow-inner"
              >
                <Icon icon="i-ri-whatsapp-line" class="size-10" />
              </div>
              <h3 class="text-lg font-bold text-n-slate-12 mb-2 tracking-tight">
                {{ t('WHATSAPP_TEMPLATES.MANAGER.SELECT_INBOX_FIRST') }}
              </h3>
              <p
                class="text-sm font-medium text-n-slate-10 max-w-sm leading-relaxed mb-6"
              >
                Selecione uma caixa de entrada no topo da página para gerenciar
                seus templates.
              </p>
            </template>
          </div>
        </Transition>

        <!-- Empty Templates Premium -->
        <Transition
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="opacity-0 translate-y-4"
        >
          <div
            v-if="selectedInboxId && !templates.length && !uiFlags.isFetching"
            class="flex flex-col items-center pt-12 pb-8 text-center max-w-md mx-auto"
          >
            <div
              class="size-20 rounded-3xl bg-n-alpha-2 flex items-center justify-center mb-5 ring-1 ring-white/10 shadow-inner"
            >
              <Icon icon="i-lucide-file-text" class="size-10 text-n-slate-9" />
            </div>
            <h3 class="text-lg font-bold text-n-slate-12 mb-2 tracking-tight">
              {{ t('WHATSAPP_TEMPLATES.MANAGER.EMPTY') }}
            </h3>
            <p
              class="text-sm font-medium text-n-slate-10 max-w-sm leading-relaxed mb-6"
            >
              {{ t('WHATSAPP_TEMPLATES.MANAGER.EMPTY_DESCRIPTION') }}
            </p>
            <div class="flex items-center gap-3">
              <button
                class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl border border-n-strong bg-n-background text-n-slate-12 transition-all hover:bg-n-alpha-1/50 shadow-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
                @click="syncTemplates"
              >
                <span
                  v-if="uiFlags.isSyncing"
                  class="i-lucide-loader-2 size-4.5 animate-spin"
                  aria-hidden="true"
                />
                <span
                  v-else
                  class="i-lucide-refresh-cw size-4.5"
                  aria-hidden="true"
                />
                {{ t('WHATSAPP_TEMPLATES.MANAGER.SYNC') }}
              </button>
              <button
                class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-n-brand hover:bg-n-brand/90 rounded-xl shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-n-brand hover:-translate-y-px active:translate-y-0"
                @click="showCreateModal = true"
              >
                <span class="i-lucide-plus size-4.5" aria-hidden="true" />
                {{ t('WHATSAPP_TEMPLATES.MANAGER.CREATE') }}
              </button>
            </div>
          </div>
        </Transition>

        <!-- No Filter Results Premium -->
        <Transition
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="opacity-0 translate-y-4"
        >
          <div
            v-if="
              selectedInboxId &&
              templates.length &&
              !filteredTemplates.length &&
              !uiFlags.isFetching
            "
            class="flex flex-col items-center pt-12 pb-8 text-center max-w-md mx-auto"
          >
            <div
              class="size-20 rounded-3xl bg-n-alpha-2 flex items-center justify-center mb-5 ring-1 ring-white/10 shadow-inner"
            >
              <Icon icon="i-lucide-search-x" class="size-10 text-n-slate-9" />
            </div>
            <h3 class="text-lg font-bold text-n-slate-12 mb-2 tracking-tight">
              Nenhum resultado encontrado
            </h3>
            <p
              class="text-sm font-medium text-n-slate-10 max-w-sm leading-relaxed mb-6"
            >
              {{ t('WHATSAPP_TEMPLATES.MANAGER.NO_FILTER_RESULTS') }}
            </p>
          </div>
        </Transition>

        <!-- Grid Cards (densified: max 3 cols + p-4 + no gradient overlay) -->
        <div
          v-if="!uiFlags.isFetching && filteredTemplates.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          <div
            v-for="tmpl in filteredTemplates"
            :key="tmpl.id"
            class="group flex flex-col p-4 rounded-xl border border-n-weak bg-n-background hover:bg-n-alpha-1/30 hover:border-n-brand/40 hover:shadow-md transition-all duration-200"
          >
            <!-- Top Section: Icon & Badges -->
            <div class="flex items-start justify-between gap-3 mb-3">
              <div
                class="flex size-10 flex-shrink-0 items-center justify-center rounded-xl ring-1"
                :class="categoryBadgeClass(tmpl.category)"
              >
                <Icon :icon="categoryIcon(tmpl.category)" class="size-4.5" />
              </div>

              <span
                class="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                :class="statusBadgeClass(tmpl.status)"
              >
                <span class="size-1.5 rounded-full bg-current" />
                {{ tmpl.status }}
              </span>
            </div>

            <!-- Content Section -->
            <div class="flex-1 min-w-0">
              <h3
                class="text-sm font-bold text-n-slate-12 font-mono leading-tight mb-1.5 truncate group-hover:text-n-brand transition-colors"
              >
                {{ tmpl.name }}
              </h3>

              <p
                v-if="getTemplateHeader(tmpl)"
                class="text-xs font-bold text-n-slate-12 mb-1 truncate"
              >
                {{ getTemplateHeader(tmpl) }}
              </p>

              <p
                class="text-xs font-medium text-n-slate-11 line-clamp-2 leading-relaxed mb-3"
              >
                {{ getTemplateBody(tmpl) }}
              </p>

              <!-- Tags/Metadata -->
              <div class="flex flex-wrap items-center gap-1.5">
                <span
                  class="inline-flex items-center gap-1 text-[11px] font-semibold text-n-slate-10 bg-n-alpha-1 px-1.5 py-0.5 rounded-md"
                >
                  <Icon icon="i-lucide-globe" class="size-3" />
                  {{ tmpl.language }}
                </span>
                <span
                  v-if="getButtonCount(tmpl) > 0"
                  class="inline-flex items-center gap-1 text-[11px] font-semibold text-n-slate-10 bg-n-alpha-1 px-1.5 py-0.5 rounded-md"
                >
                  <Icon icon="i-lucide-mouse-pointer-click" class="size-3" />
                  {{ getButtonCount(tmpl) }}
                  {{ getButtonCount(tmpl) === 1 ? 'Btn' : 'Btns' }}
                </span>
              </div>

              <!-- Rejected Warning -->
              <div
                v-if="tmpl.rejected_reason"
                class="mt-2.5 flex items-start gap-2 p-2 rounded-lg bg-n-ruby-2 border border-n-ruby-4"
              >
                <Icon
                  icon="i-lucide-alert-circle"
                  class="size-3.5 text-n-ruby-11 shrink-0 mt-px"
                />
                <span
                  class="text-[11px] font-semibold text-n-ruby-11 leading-tight line-clamp-2"
                >
                  {{ tmpl.rejected_reason }}
                </span>
              </div>
            </div>

            <!-- Actions (always visible — touch-friendly, no hover-only) -->
            <div
              class="flex items-center gap-1 pt-3 mt-3 border-t border-n-weak/50"
            >
              <button
                class="flex-1 inline-flex items-center justify-center gap-1.5 min-h-9 px-2 text-xs font-semibold rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors focus:outline-none focus:ring-2 focus:ring-n-slate-6"
                :aria-label="t('WHATSAPP_TEMPLATES.MANAGER.EDIT')"
                @click="editTemplate(tmpl)"
              >
                <Icon icon="i-lucide-pencil" class="size-3.5" />
                {{ t('WHATSAPP_TEMPLATES.MANAGER.EDIT') }}
              </button>
              <div class="w-px h-4 bg-n-weak/50" />
              <button
                class="flex-1 inline-flex items-center justify-center gap-1.5 min-h-9 px-2 text-xs font-semibold rounded-lg text-n-ruby-10 hover:bg-n-ruby-2 hover:text-n-ruby-11 transition-colors focus:outline-none focus:ring-2 focus:ring-n-ruby-6"
                :aria-label="t('WHATSAPP_TEMPLATES.MANAGER.DELETE_TITLE')"
                @click="confirmDelete(tmpl)"
              >
                <Icon icon="i-lucide-trash-2" class="size-3.5" />
                {{ t('WHATSAPP_TEMPLATES.MANAGER.DELETE_TITLE') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TemplateForm is self-contained with its own overlay — render directly -->
    <TemplateForm
      v-if="showCreateModal"
      :inbox-id="selectedInboxId"
      @saved="onTemplateCreated"
      @close="showCreateModal = false"
    />

    <TemplateForm
      v-if="showEditModal"
      :inbox-id="selectedInboxId"
      :template="templateToEdit"
      @saved="onTemplateUpdated"
      @close="showEditModal = false"
    />

    <!-- woot-delete-modal is typically styled by global settings, mas pode ser refeito se quisermos. Mantemos para integridade -->
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="deleteTemplate"
      :title="t('WHATSAPP_TEMPLATES.MANAGER.DELETE_TITLE')"
      :message="
        t('WHATSAPP_TEMPLATES.MANAGER.DELETE_MESSAGE', {
          name: templateToDelete?.name,
          language: templateToDelete?.language,
        })
      "
      :confirm-text="t('WHATSAPP_TEMPLATES.MANAGER.DELETE_CONFIRM')"
      :reject-text="t('WHATSAPP_TEMPLATES.MANAGER.CANCEL')"
    />
  </div>
</template>
